import React from 'react'
import {
  Box,
  useResponsiveDropdown,
  Dropdown,
  useModalContext,
  LoadingPlaceholder,
  Flex,
  Tooltip,
} from '@sketch/components'
import SharingModal from '../../modals/SharingModal'
import { PartialShareModal } from '../../modals/PartialShareModal'
import {
  TriggerButton,
  DropdownItemStyled,
  DropdownHeader,
  DropdownItemDescription,
  PlusIcon,
  PreviewHeader,
  LinkIcon,
  SlidersIcon,
  ActionsWrapper,
  ActionButton,
  PreviewItem,
  PreviewList,
  ItemName,
  NewButton,
} from './PartialShareDropdown.styles'
import { ShareWithoutVersion, useVersioning } from '../../../versioning'
import { useGetSharePreviewsQuery } from '@sketch/gql-types'

interface DropdownContentProps {
  share: ShareWithoutVersion
  hide: (trigger: string) => void
}

const PreviewArea = ({ share, hide }: DropdownContentProps) => {
  const { showModal } = useModalContext()
  const { latestVersionId } = useVersioning()
  const { data, loading, error } = useGetSharePreviewsQuery({
    variables: { shareIdentifier: share.identifier },
  })

  if (loading) {
    return (
      <Flex height={52} justifyContent="center">
        <LoadingPlaceholder size="24px" />
      </Flex>
    )
  }

  if (error) {
    // @TODO: add error case
    return (
      <Flex height={52} justifyContent="center">
        An error occurred
      </Flex>
    )
  }

  const showPreviewCreationModal = () => {
    showModal(PartialShareModal, {
      shareIdentifier: share.identifier,
      shareName: share.name,
      versionShortId: latestVersionId,
    })
    hide('opened-modal')
  }

  if (!data || data?.share?.previews.length === 0) {
    return (
      <DropdownItemStyled onClick={showPreviewCreationModal}>
        Share a Preview
        <DropdownItemDescription>
          Use previews to share only specific pages and prototypes.
        </DropdownItemDescription>
      </DropdownItemStyled>
    )
  }

  const previews = data.share?.previews || []

  const handleCopyLink = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    // @TODO: for when we have the links
    alert('link is coming soon')
  }

  const handleEdit = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    alert('edit is coming soon')
  }

  return (
    <>
      <PreviewHeader>
        <span>Previews</span>
        <Tooltip placement="top" content="Share a Preview…">
          <NewButton onClick={showPreviewCreationModal}>
            <PlusIcon />
          </NewButton>
        </Tooltip>
      </PreviewHeader>
      <PreviewList>
        {previews?.map(preview =>
          !preview ? null : (
            <PreviewItem
              key={preview.identifier}
              onClick={() =>
                // @TODO: for when we have the links
                alert('open link')
              }
            >
              <ItemName>{preview.name}</ItemName>
              <ActionsWrapper>
                <Tooltip placement="top" content="Copy Link">
                  <ActionButton onClick={handleCopyLink}>
                    <LinkIcon />
                  </ActionButton>
                </Tooltip>
                <Tooltip placement="top" content="Edit Preview…">
                  <ActionButton onClick={handleEdit}>
                    <SlidersIcon />
                  </ActionButton>
                </Tooltip>
              </ActionsWrapper>
            </PreviewItem>
          )
        )}
        {previews?.map(preview =>
          !preview ? null : (
            <PreviewItem
              key={preview.identifier}
              onClick={() => alert('open link')}
            >
              <ItemName>{preview.name}</ItemName>
              <ActionsWrapper>
                <Tooltip placement="top" content="Copy Link">
                  <ActionButton onClick={handleCopyLink}>
                    <LinkIcon />
                  </ActionButton>
                </Tooltip>
                <Tooltip placement="top" content="Edit Preview…">
                  <ActionButton onClick={handleEdit}>
                    <SlidersIcon />
                  </ActionButton>
                </Tooltip>
              </ActionsWrapper>
            </PreviewItem>
          )
        )}
      </PreviewList>
    </>
  )
}

const DropdownContent = ({ share, hide }: DropdownContentProps) => {
  const { showModal } = useModalContext()

  return (
    <Box width={256}>
      <DropdownHeader>Share Document…</DropdownHeader>
      <DropdownItemStyled
        onClick={() => {
          showModal(SharingModal, {
            identifier: share.identifier,
            name: share.name,
          })
          hide('opened-modal')
        }}
      >
        Edit Permissions…
      </DropdownItemStyled>
      <Dropdown.Divider />
      <PreviewArea share={share} hide={hide} />
    </Box>
  )
}

interface PartialShareDropdownProps {
  share: ShareWithoutVersion
}

export const PartialShareDropdown = ({ share }: PartialShareDropdownProps) => {
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: DropdownContent,
    dropdownProps: { share },
    placement: 'bottom-end',
  })

  return (
    <>
      <TriggerButton {...buttonProps} data-testid="partial-sharing-button">
        Share…
      </TriggerButton>
      {content}
    </>
  )
}
